import { GameAttributes, GameEvent, User } from '@/types'
import { request } from '@/utils/requests/request'

import { MessageAction, OutcomingMessage } from '../main-frame/message-types'

export interface Order {
    amount: number
    gameHru: string
    gameTitle: string
    type: 'order'
    attributes: GameAttributes
    description?: string
    subtitle?: string
    photoUrl?: string
    externalId?: string
}

export type DisableAdOrder = {
    amount: number
    type: 'disable-ad-order'
    durationInDays: 1 | 7 | 30
    externalId: string
}

export interface Topup {
    amount?: number
    type: 'topup'
}

export interface PurchaseStatus {
    status: 'PAID' | 'FAILED'
    orderId: string
    amount: number
    externalId?: string
}

export type ChargeResponse<T extends Order | DisableAdOrder | Topup> = T extends Order | DisableAdOrder
    ? Awaited<ReturnType<typeof processPurchase>>
    : User | null

export const processPurchase = (data: Order | DisableAdOrder, url?: string) => {
    let body: string

    if (data.type === 'order') {
        const { gameHru, attributes, ...orderObject } = data
        body = JSON.stringify({ order: orderObject, gameHru, attributes })
    } else {
        const { amount, durationInDays, externalId } = data
        body = JSON.stringify({ order: { amount, durationInDays, externalId } })
    }

    return request<PurchaseStatus>(url || '/api/v1/in-game-payments/purchase', {
        method: 'POST',
        body,
    })
}

const purchaseMessage = (res: Awaited<ReturnType<typeof request<PurchaseStatus>>>, event: GameEvent) => {
    let payload = res.data
    if (res.errors.length) {
        payload = {
            status: 'FAILED',
            error: res.errors.join(', '),
        }
    }

    return {
        type: 'in-game-payments',
        action: MessageAction.purchaseStatus,
        originalMessageEvent: event,
        payload,
    } as OutcomingMessage
}

export type PaymentModalOrderResponse<T = Order | DisableAdOrder | Topup | null> = T extends
    | Order
    | DisableAdOrder
    | Topup
    ? Promise<ChargeResponse<T>>
    : undefined

export type ChargeUserFunction = <T extends Order | Topup | null>(value: T) => PaymentModalOrderResponse<T>

export const createInGamePayments = (
    gameHru: string,
    gameTitle: string,
    chargeUser: ChargeUserFunction,
    attributes: GameAttributes,
) => {
    const messageHandler = async (event: GameEvent) => {
        switch (event.data.action) {
            case MessageAction.requestPurchase: {
                try {
                    const purchaseData = await chargeUser({
                        type: 'order',
                        gameHru,
                        gameTitle,
                        attributes,
                        ...event.data.payload,
                    } as Order)
                    return purchaseMessage(purchaseData, event)
                } catch (e) {
                    console.error('Caught error during purchase request:', e)
                    return purchaseMessage(
                        {
                            errors: [e?.toString()],
                            data: null,
                            // @ts-expect-error fill later
                            originalResponse: null,
                        },
                        event,
                    )
                }
            }
            case MessageAction.requestTopup: {
                try {
                    const userInfo = await chargeUser({ type: 'topup', ...event.data.payload } as Topup)
                    return {
                        type: 'in-game-payments',
                        action: MessageAction.topupStatus,
                        originalMessageEvent: event as GameEvent,
                        payload: userInfo,
                    } as OutcomingMessage
                } catch (e) {
                    return purchaseMessage(
                        {
                            errors: [e?.toString()],
                            data: null,
                            // @ts-expect-error fill later
                            originalResponse: null,
                        },
                        event,
                    )
                }
            }
            default:
                return {
                    action: MessageAction.unknownMessageType,
                    originalMessageEvent: event as GameEvent,
                    payload: {
                        error: `Unknown message action "${event.data.action}"`,
                    },
                    type: 'error',
                } as OutcomingMessage
        }
    }

    return messageHandler
}

export type InGamePaymentsMessageHandler = ReturnType<typeof createInGamePayments>
